import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { isAbortError } from '@mbbe/config';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastInput {
  title?: string;
  description?: string;
  duration?: number;
  id?: string;
}

export interface ToastItem extends Required<Pick<ToastInput, 'id' | 'duration'>> {
  variant: ToastVariant;
  title: string;
  description?: string;
  /**
   * Lifetime generation assigned by ToastProvider. A repeated id receives a new revision, so the
   * viewport restarts the progress indicator and an older expiry can never dismiss the new toast.
   */
  revision?: number;
}

/** Input that holds a toast open: pointer hover or keyboard focus inside the toast. */
export type ToastHoldSource = 'pointer' | 'focus';

interface ToastContextValue {
  show: (variant: ToastVariant, input: string | ToastInput) => string;
  success: (input: string | ToastInput) => string;
  /** Pass the original mutation failure so session cancellation stays silent. */
  error: (input: string | ToastInput, cause?: unknown) => string | undefined;
  warning: (input: string | ToastInput) => string;
  info: (input: string | ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const TOAST_EVENT = 'mbbe:toast';
const DEFAULT_DURATION = 5200;
const MAX_TOASTS = 5;
/** setTimeout overflows (fires immediately) above this delay. */
const MAX_TIMER_DELAY = 2_147_483_647;
/** After hover/focus ends, a toast stays visible for at least this long (capped by its duration). */
const RESUME_GRACE_MS = 1000;

interface TimerEntry {
  revision: number;
  duration: number;
  remaining: number;
  startedAt: number | null;
  handle: number | null;
}

interface HoldState {
  pointer: boolean;
  focus: boolean;
}

/** Only finite positive durations are timed; 0, negative, NaN and Infinity are persistent. */
function normalizeDuration(duration: number | undefined): number {
  if (duration === undefined) return DEFAULT_DURATION;
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return Math.min(duration, MAX_TIMER_DELAY);
}

const variantDefaults: Record<ToastVariant, string> = {
  success: 'Başarılı',
  error: 'Başarısız',
  warning: 'Uyarı',
  info: 'Bilgi',
};

const variantClasses: Record<ToastVariant, string> = {
  success: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200',
  error: 'border-red-400/30 bg-red-500/10 text-red-200',
  warning: 'border-amber-400/30 bg-amber-500/10 text-amber-100',
  info: 'border-brand-400/30 bg-brand-500/10 text-brand-100',
};

const dotClasses: Record<ToastVariant, string> = {
  success: 'bg-emerald-300 shadow-[0_0_18px_rgba(110,231,183,0.45)]',
  error: 'bg-red-300 shadow-[0_0_18px_rgba(252,165,165,0.45)]',
  warning: 'bg-amber-300 shadow-[0_0_18px_rgba(252,211,77,0.45)]',
  info: 'bg-brand-300 shadow-[0_0_18px_rgba(147,197,253,0.45)]',
};

function createId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeToast(variant: ToastVariant, input: string | ToastInput): ToastItem {
  const options = typeof input === 'string' ? { description: input } : input;
  return {
    id: options.id ?? createId(),
    variant,
    title: options.title ?? variantDefaults[variant],
    description: options.description,
    duration: normalizeDuration(options.duration),
  };
}

function emitToast(variant: ToastVariant, input: string | ToastInput): string {
  const toast = normalizeToast(variant, input);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<ToastItem>(TOAST_EVENT, { detail: toast }));
  }
  return toast.id;
}

const fallbackApi: ToastContextValue = {
  show: emitToast,
  success: (input) => emitToast('success', input),
  error: (input, cause) => isAbortError(cause) ? undefined : emitToast('error', input),
  warning: (input) => emitToast('warning', input),
  info: (input) => emitToast('info', input),
  dismiss: (id) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<string>(`${TOAST_EVENT}:dismiss`, { detail: id }));
    }
  },
};

export function useToast(): ToastContextValue {
  return useContext(ToastContext) ?? fallbackApi;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // The ref is the source of truth for the visible list so timer bookkeeping never runs inside a
  // state updater (updaters must be pure and are double-invoked in React Strict Mode).
  const toastsRef = useRef<ToastItem[]>([]);
  // Exactly one entry per visible timed toast; persistent toasts and removed toasts have none.
  const timersRef = useRef<Map<string, TimerEntry>>(new Map());
  const holdsRef = useRef<Map<string, HoldState>>(new Map());
  const revisionRef = useRef(0);
  const disposedRef = useRef(false);

  const clearEntry = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (entry?.handle != null) window.clearTimeout(entry.handle);
    timersRef.current.delete(id);
  }, []);

  const commit = useCallback((next: ToastItem[]) => {
    toastsRef.current = next;
    const visible = new Set(next.map((item) => item.id));
    for (const id of Array.from(timersRef.current.keys())) {
      if (!visible.has(id)) clearEntry(id);
    }
    for (const id of Array.from(holdsRef.current.keys())) {
      if (!visible.has(id)) holdsRef.current.delete(id);
    }
    setToasts(next);
  }, [clearEntry]);

  const dismiss = useCallback((id: string) => {
    if (!toastsRef.current.some((toast) => toast.id === id)) {
      clearEntry(id);
      holdsRef.current.delete(id);
      return;
    }
    commit(toastsRef.current.filter((toast) => toast.id !== id));
  }, [clearEntry, commit]);

  const isHeld = useCallback((id: string) => {
    const hold = holdsRef.current.get(id);
    return Boolean(hold?.pointer || hold?.focus);
  }, []);

  const armTimer = useCallback((id: string, entry: TimerEntry, delay: number) => {
    entry.startedAt = Date.now();
    entry.handle = window.setTimeout(() => {
      // Only the entry of the current generation may dismiss; stale callbacks do nothing.
      const current = timersRef.current.get(id);
      if (!current || current !== entry || current.revision !== entry.revision) return;
      entry.handle = null;
      if (isHeld(id)) {
        entry.remaining = 0;
        entry.startedAt = null;
        return;
      }
      dismiss(id);
    }, delay);
  }, [dismiss, isHeld]);

  const startEntry = useCallback((toast: ToastItem) => {
    clearEntry(toast.id);
    if (toast.duration <= 0) return;
    const entry: TimerEntry = {
      revision: toast.revision ?? 0,
      duration: toast.duration,
      remaining: toast.duration,
      startedAt: null,
      handle: null,
    };
    timersRef.current.set(toast.id, entry);
    if (!isHeld(toast.id)) armTimer(toast.id, entry, entry.remaining);
  }, [armTimer, clearEntry, isHeld]);

  const addToast = useCallback((incoming: ToastItem) => {
    if (disposedRef.current) return;
    const toast: ToastItem = { ...incoming, revision: ++revisionRef.current };
    const current = toastsRef.current;
    const index = current.findIndex((item) => item.id === toast.id);
    let next: ToastItem[];
    if (index >= 0) {
      // Replace in place: keeps the DOM node (and keyboard focus) and the visible order stable.
      next = current.slice();
      next[index] = toast;
    } else {
      next = [toast, ...current];
      while (next.length > MAX_TOASTS) {
        // Evict the oldest toast, but never the one whose controls currently hold keyboard focus.
        let evict = next.length - 1;
        while (evict > 0 && holdsRef.current.get(next[evict].id)?.focus) evict -= 1;
        next.splice(evict, 1);
      }
    }
    commit(next);
    startEntry(toast);
  }, [commit, startEntry]);

  const setHold = useCallback((id: string, source: ToastHoldSource, held: boolean) => {
    if (!toastsRef.current.some((toast) => toast.id === id)) return;
    const hold = holdsRef.current.get(id) ?? { pointer: false, focus: false };
    hold[source] = held;
    if (hold.pointer || hold.focus) holdsRef.current.set(id, hold);
    else holdsRef.current.delete(id);
    const entry = timersRef.current.get(id);
    if (!entry) return;
    if (hold.pointer || hold.focus) {
      if (entry.handle != null) {
        window.clearTimeout(entry.handle);
        entry.handle = null;
        entry.remaining = Math.max(0, entry.remaining - (Date.now() - (entry.startedAt ?? Date.now())));
        entry.startedAt = null;
      }
    } else if (entry.handle == null) {
      armTimer(id, entry, Math.max(entry.remaining, Math.min(entry.duration, RESUME_GRACE_MS)));
    }
  }, [armTimer]);

  const show = useCallback((variant: ToastVariant, input: string | ToastInput) => {
    const toast = normalizeToast(variant, input);
    addToast(toast);
    return toast.id;
  }, [addToast]);

  const api = useMemo<ToastContextValue>(() => ({
    show,
    success: (input) => show('success', input),
    error: (input, cause) => isAbortError(cause) ? undefined : show('error', input),
    warning: (input) => show('warning', input),
    info: (input) => show('info', input),
    dismiss,
  }), [show, dismiss]);

  useEffect(() => {
    disposedRef.current = false;
    // React Strict Mode runs cleanup and setup again on the same state: re-arm timers for toasts
    // that are still visible so none is left without an expiry.
    for (const toast of toastsRef.current) {
      if (!timersRef.current.has(toast.id)) startEntry(toast);
    }
    const handleToast = (event: Event) => addToast((event as CustomEvent<ToastItem>).detail);
    const handleDismiss = (event: Event) => dismiss((event as CustomEvent<string>).detail);
    window.addEventListener(TOAST_EVENT, handleToast);
    window.addEventListener(`${TOAST_EVENT}:dismiss`, handleDismiss);
    const timers = timersRef.current;
    return () => {
      disposedRef.current = true;
      window.removeEventListener(TOAST_EVENT, handleToast);
      window.removeEventListener(`${TOAST_EVENT}:dismiss`, handleDismiss);
      timers.forEach((entry) => {
        if (entry.handle != null) window.clearTimeout(entry.handle);
      });
      timers.clear();
    };
  }, [addToast, dismiss, startEntry]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} onHoldChange={setHold} />
    </ToastContext.Provider>
  );
}

/** Reserve the measured space of a visible bottom sheet/banner, including responsive reflow. */
function useBottomSurfaceOffset() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const observed = new Set<Element>();
    const measure = () => {
      let nextOffset = 0;
      const surfaces = document.querySelectorAll('[data-toast-bottom-surface]');
      for (const surface of surfaces) {
        if (!observed.has(surface)) {
          observer?.observe(surface);
          observed.add(surface);
        }
        const rect = surface.getBoundingClientRect();
        // The Destek panel becomes an absolute dropdown on desktop; only bottom-fixed surfaces
        // reserve space. Hidden/unmounted surfaces immediately release their reservation.
        if (getComputedStyle(surface).position === 'fixed' && rect.height > 0
          && rect.bottom >= window.innerHeight - 1) {
          nextOffset = Math.max(nextOffset, window.innerHeight - rect.top + 12);
        }
      }
      for (const surface of observed) {
        if (!surface.isConnected) {
          observer?.unobserve(surface);
          observed.delete(surface);
        }
      }
      setOffset(nextOffset);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    const mutations = new MutationObserver(measure);
    mutations.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    measure();
    return () => {
      observer?.disconnect();
      mutations.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  return offset;
}

export function ToastViewport({
  toasts,
  onDismiss,
  onHoldChange,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  /** Reports pointer hover / keyboard focus entering or leaving a toast so its expiry can pause. */
  onHoldChange?: (id: string, source: ToastHoldSource, held: boolean) => void;
}) {
  const bottomOffset = useBottomSurfaceOffset();
  return (
    <div
      style={bottomOffset ? { bottom: bottomOffset, maxHeight: `calc(100dvh - ${bottomOffset + 12}px)`, overflowY: 'auto' } : undefined}
      aria-live="polite"
      aria-atomic="false"
      data-dialog-persistent=""
      className="pointer-events-none fixed right-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-[1000] flex w-[calc(100vw-1.5rem)] max-w-[390px] flex-col gap-2 sm:right-5 sm:bottom-[max(1.25rem,env(safe-area-inset-bottom))]"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          data-toast-id={toast.id}
          onPointerEnter={() => onHoldChange?.(toast.id, 'pointer', true)}
          onPointerLeave={() => onHoldChange?.(toast.id, 'pointer', false)}
          onFocus={() => onHoldChange?.(toast.id, 'focus', true)}
          onBlur={(event) => {
            if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
            onHoldChange?.(toast.id, 'focus', false);
          }}
          className={`toast-item toast-enter pointer-events-auto overflow-hidden rounded-lg border bg-slate-950/95 shadow-2xl shadow-black/30 backdrop-blur-xl ${variantClasses[toast.variant]}`}
        >
          <div className="flex gap-3 px-3.5 py-3">
            <span className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${dotClasses[toast.variant]}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-5 text-white">{toast.title}</div>
              {toast.description && (
                <div className="mt-0.5 break-words text-sm leading-5 text-slate-300">{toast.description}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              aria-label="Bildirimi kapat"
              className="flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <span aria-hidden>x</span>
            </button>
          </div>
          {toast.duration > 0 && (
            <div
              key={toast.revision ?? 0}
              data-toast-progress=""
              className="toast-progress h-0.5 origin-left bg-current opacity-45"
              style={{ '--toast-duration': `${toast.duration}ms` } as React.CSSProperties}
            />
          )}
        </div>
      ))}
    </div>
  );
}
